/**
 * Audit log — who changed what, when, and why.
 *
 * Append-only: entries are written by database triggers and by the admin
 * console, and can never be edited from the app.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, RefreshCw, Search } from "lucide-react";
import { downloadBlob, toCsv } from "@/lib/couponConsole";

interface AuditEntry {
  id: string;
  coupon_id: string | null;
  coupon_code: string;
  event_type: string;
  actor_email: string | null;
  reason: string | null;
  old_values: unknown;
  new_values: unknown;
  created_at: string;
}

const EVENT_TONE: Record<string, string> = {
  create: "bg-primary/15 text-primary",
  update: "bg-muted text-foreground",
  activate: "bg-primary/15 text-primary",
  deactivate: "bg-muted text-muted-foreground",
  pause: "bg-muted text-muted-foreground",
  archive: "bg-muted text-muted-foreground",
  duplicate: "bg-muted text-foreground",
  soft_delete: "bg-destructive/15 text-destructive",
  hard_delete: "bg-destructive/20 text-destructive",
  restore: "bg-primary/15 text-primary",
  mrp_change_request: "bg-destructive/15 text-destructive",
  redeem: "bg-primary/10 text-primary",
};

/** Field-level difference between the before and after snapshots. */
function changedFields(oldValues: unknown, newValues: unknown): string[] {
  if (!oldValues || !newValues || typeof oldValues !== "object" || typeof newValues !== "object") {
    return [];
  }
  const before = oldValues as Record<string, unknown>;
  const after = newValues as Record<string, unknown>;
  const skip = new Set(["updated_at", "created_at", "id"]);
  return Object.keys(after)
    .filter((k) => !skip.has(k) && JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => `${k.replace(/_/g, " ")}: ${fmt(before[k])} → ${fmt(after[k])}`);
}

const fmt = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
};

const CouponAuditLog = ({ couponId }: { couponId?: string }) => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    let request = supabase
      .from("coupon_audit_log")
      .select(
        "id, coupon_id, coupon_code, event_type, actor_email, reason, old_values, new_values, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(300);
    if (couponId) request = request.eq("coupon_id", couponId);
    const { data, error } = await request;
    if (error) toast.error("Could not load the audit log", { description: error.message });
    setEntries((data ?? []) as AuditEntry[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.coupon_code, e.event_type, e.actor_email, e.reason]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [entries, query]);

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.info("Nothing to export.");
      return;
    }
    downloadBlob(
      "benefits-audit-log.csv",
      new Blob(
        [
          toCsv(
            filtered.map((e) => ({
              when: e.created_at,
              code: e.coupon_code,
              action: e.event_type,
              admin: e.actor_email ?? "",
              reason: e.reason ?? "",
              changes: changedFields(e.old_values, e.new_values).join(" | "),
            })),
          ),
        ],
        { type: "text/csv;charset=utf-8" },
      ),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by code, action, admin or reason"
            className="pl-9 min-h-11"
            aria-label="Search the audit log"
          />
        </div>
        <Button variant="outline" className="min-h-11" onClick={() => void load()}>
          <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
          Refresh
        </Button>
        <Button variant="outline" className="min-h-11" onClick={exportCsv}>
          <Download className="w-4 h-4 mr-2" aria-hidden="true" />
          Export
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-3xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-3xl">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Nothing here yet. Every change you make from now on is recorded.
          </CardContent>
        </Card>
      ) : (
        <ol className="space-y-3">
          {filtered.map((e) => {
            const changes = changedFields(e.old_values, e.new_values);
            return (
              <li key={e.id}>
                <Card className="rounded-3xl">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={EVENT_TONE[e.event_type] ?? "bg-muted text-foreground"}>
                        {e.event_type.replace(/_/g, " ")}
                      </Badge>
                      <span className="font-mono text-sm">{e.coupon_code}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(e.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {e.actor_email ?? "System"}
                      {e.reason ? ` — ${e.reason}` : ""}
                    </p>
                    {changes.length > 0 && (
                      <ul className="text-xs text-muted-foreground space-y-0.5">
                        {changes.slice(0, 6).map((c) => (
                          <li key={c} className="font-mono break-words">{c}</li>
                        ))}
                        {changes.length > 6 && <li>+{changes.length - 6} more fields</li>}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default CouponAuditLog;
